import { useQuery } from '@tanstack/react-query';
import { get } from '../api/client';
import { isMissing } from '../api/queries';
import type { CmdLogRow } from '../api/types';
import { useLive } from '../live/store';

export const cmdLogKey = (serial: string, kind: string, name: string) => ['robot-log', serial, kind, name] as const;

/**
 * Rows from the server (last 100 of the kind), merged with rows pushed live over /ws/ui; newest first.
 * The name filter is applied here as a substring match (the server's `name=` is an exact match).
 */
export function useCmdLog(serial: string, kind: '' | 'cmd' | 'event', name: string) {
  const q = useQuery({
    queryKey: cmdLogKey(serial, kind, ''),
    queryFn: async () => {
      const u = new URLSearchParams({ limit: '100' });
      if (kind) u.set('kind', kind);
      const r = await get<unknown>(`/api/robots/${encodeURIComponent(serial)}/log?${u.toString()}`);
      const list = Array.isArray(r) ? r : ((r as { items?: unknown[]; rows?: unknown[] })?.items ?? (r as { rows?: unknown[] })?.rows ?? []);
      return (list as CmdLogRow[]).filter((x) => x && typeof x === 'object' && typeof x.id === 'number');
    },
    retry: (n, e) => !isMissing(e) && n < 1,
    staleTime: 15_000,
  });
  const live = useLive((s) => s.cmdLog[serial]);
  const needle = name.trim().toLowerCase();
  const rows = [...(live ?? []).filter((r) => !kind || r.kind === kind), ...(q.data ?? [])].filter((r) => !needle || r.name.toLowerCase().includes(needle));
  const seen = new Set<number>();
  const merged = rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true))).sort((a, b) => b.id - a.id);
  return { rows: merged, loading: q.isLoading, error: q.error, missing: isMissing(q.error), refetch: q.refetch };
}

/** Secrets never reach the screen: token / password / secret / key fields are masked in args and data. */
export function maskSecrets(v: unknown, depth = 0): unknown {
  if (depth > 6) return v;
  if (Array.isArray(v)) return v.map((x) => maskSecrets(x, depth + 1));
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, x] of Object.entries(v as Record<string, unknown>)) out[k] = /token|secret|password|passwd|apikey|api_key|authorization/i.test(k) && typeof x === 'string' ? '***' : maskSecrets(x, depth + 1);
    return out;
  }
  if (typeof v === 'string' && /^eyJ[A-Za-z0-9_-]{20,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(v)) return '***';
  return v;
}

export interface AppLog {
  lines: string[];
  files: string[];
}

/** The robot app's log tail (the server asks the robot for it). */
export async function fetchAppLog(serial: string, lines: number, grep: string): Promise<AppLog> {
  const u = new URLSearchParams({ lines: String(lines) });
  if (grep.trim()) u.set('grep', grep.trim());
  const r = await get<{ lines?: unknown; files?: unknown } | string[] | null>(`/api/robots/${encodeURIComponent(serial)}/applog?${u.toString()}`);
  if (Array.isArray(r)) return { lines: r.map(String), files: [] };
  return { lines: Array.isArray(r?.lines) ? r!.lines.map(String) : [], files: Array.isArray(r?.files) ? r!.files.map(String) : [] };
}
